import { makeStyles } from "@material-ui/core";
import React, { useEffect, useState } from "react";
import Loading from "../../../../common-components/Loading";
import { getPMRForecastAnalysisAPI } from "../../../../api/reports-api";
import {
  TABLE_FONT,
  TH_BG,
  CELL_FG,
  EVEN_BG,
  ODD_BG,
  TH_BG_BAND,
  CELL_FG_STRONG,
} from "../../reportTableConfig";


const HEADER_GROUP = TH_BG_BAND;
const HEADER_COL = TH_BG;
const POS_C        = "#10B981";
const NEG_C        = "#D64550";
const TOTAL_FG = CELL_FG_STRONG;
const FONT = TABLE_FONT;

const TH_SH_BOT_G = "inset 0 -2px 0 rgba(255,255,255,.22)";
const TH_SH_BOT   = "inset 0 -1px 0 rgba(255,255,255,.12)";

/** Build one comparison table's rows from the shared lineItems + a comparison block. */
function buildForecastRows(lineItems, comparison = {}) {
  const baseline    = comparison.baseline    || {};
  const ytdActuals  = comparison.ytdActuals  || {};
  const forecastEtc = comparison.forecastEtc || {};
  const eac         = comparison.eac         || {};
  const variance    = comparison.variance    || {};
  return lineItems.map((li) => {
    const isPct = !!li.isPct;
    const scale = (v) => (isPct && v != null ? v * 100 : v);
    return {
      label:    li.label,
      isPct,
      compare:  scale(baseline[li.key]),
      ytd:      scale(ytdActuals[li.key]),
      etc:      scale(forecastEtc[li.key]),
      eac:      scale(eac[li.key]),
      variance: scale(variance[li.key]),
    };
  });
}

/** Reshape the flat PMR API response into the two-table shape this page renders. */
function mapForecastAnalysis(d) {
  const lineItems = d.lineItems || [];
  const priorLabel = d.priorForecastVersion?.name ?? "Prior";
  return {
    year: d.fiscalYear,
    tables: [
      {
        title: `FORECAST COMPARISON (Live Forecast to Budget) - ${d.fiscalYear}`,
        compareLabel: "Total Budget",
        rows: buildForecastRows(lineItems, d.budgetComparison),
      },
      {
        title: `FORECAST COMPARISON (Live EAC to ${priorLabel} Forecast) - ${d.fiscalYear}`,
        compareLabel: "Prior Forecast",
        rows: buildForecastRows(lineItems, d.priorForecastComparison),
      },
    ],
  };
}

function fmtAmount(v) {
  if (v == null || v === "") return "";
  const n = Number(v);
  const abs = Math.abs(n).toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 });
  return n < 0 ? `($${abs})` : `$${abs}`;
}

function fmtCell(row, key) {
  const v = row[key];
  if (v == null || v === "") return "";
  if (row.isPct) {
    if (key === "variance") {
      const sign = v > 0 ? "+" : "";
      return `${sign}${Number(v).toFixed(1)}%`;
    }
    return `${Number(v).toFixed(1)}%`;
  }
  return fmtAmount(v);
}

function ForecastTable({ table, classes }) {
  const cols = [
    { key: "compare",  label: table.compareLabel },
    { key: "ytd",      label: "YTD Actuals" },
    { key: "etc",      label: "Forecast (ETC)" },
    { key: "eac",      label: "Est. At Completion (EAC)" },
    { key: "variance", label: "Variance" },
  ];

  return (
    <div className={classes.tableCard}>
      <div className={classes.tableSectionHead}>
        <h2 className={classes.tableSectionTitle}>{table.title}</h2>
      </div>
      <div className={classes.outerWrap}>
        <table style={{
          borderCollapse: "collapse",
          borderSpacing: 0,
          tableLayout: "auto",
          fontFamily: FONT,
          fontSize: 12,
          width: "100%",
        }}>
          <thead>
            {/* Group header */}
            <tr style={{ height: 32 }}>
              <th style={{ ...thFixed(), minWidth: 160 }}>EAC INFO</th>
              {cols.map((col) => (
                <th key={col.key} style={thBand()}>
                  {col.label}
                </th>
              ))}
            </tr>
            {/* — no second row needed — */}
          </thead>
          <tbody>
            {table.rows.map((row, i) => {
              const bg = i % 2 === 0 ? EVEN_BG : ODD_BG;
              const isVar = (key) => key === "variance";
              return (
                <tr key={row.label} style={{ height: 36 }}>
                  <td style={{
                    ...tdBase(bg, "left"),
                    position: "sticky",
                    left: 0,
                    zIndex: 2,
                    background: bg,
                    fontWeight: 500,
                    color: "#111827",
                    boxShadow: "4px 0 8px -2px rgba(0,0,0,0.06)",
                  }}>
                    {row.label}
                  </td>
                  {cols.map((col) => {
                    const v = row[col.key];
                    const neg = !row.isPct && v < 0;
                    const varColor = isVar(col.key)
                      ? (v > 0 ? POS_C : v < 0 ? NEG_C : CELL_FG)
                      : neg ? NEG_C : CELL_FG;
                    return (
                      <td key={col.key} style={{
                        ...tdBase(bg, "right"),
                        color: varColor,
                        fontWeight: isVar(col.key) && v !== 0 ? 700 : 400,
                      }}>
                        {fmtCell(row, col.key)}
                      </td>
                    );
                  })}
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
}

/* ── Inline style functions ── */
const thCore = (h, z, padding, bg) => ({
  position: "sticky",
  top: 0,
  zIndex: z,
  height: h,
  lineHeight: 1.25,
  boxSizing: "border-box",
  background: bg,
  color: "#fff",
  verticalAlign: "middle",
  padding,
  whiteSpace: "nowrap",
  border: "none",
});

const thFixed = () => ({
  ...thCore(32, 5, 0, HEADER_GROUP),
  left: 0,
  boxShadow: TH_SH_BOT_G,
});

const thBand = () => ({
  ...thCore(32, 3, "7px 12px", HEADER_COL),
  fontSize: 10,
  fontWeight: 600,
  letterSpacing: "0.06em",
  textTransform: "uppercase",
  textAlign: "right",
  minWidth: 130,
  boxShadow: TH_SH_BOT,
});

const tdBase = (bg, align = "right") => ({
  boxSizing: "border-box",
  background: bg,
  textAlign: align,
  fontWeight: 400,
  padding: "7px 12px",
  fontSize: 12,
  color: CELL_FG,
  whiteSpace: "nowrap",
  overflow: "hidden",
  verticalAlign: "middle",
  fontVariantNumeric: "tabular-nums",
  borderBottom: "1px solid #E2E8F0",
});

const useStyles = makeStyles(() => ({
  root: { fontFamily: FONT, paddingBottom: 24 },
  tableCard: {
    background: "#fff",
    borderRadius: 12,
    border: "1px solid #EBEBEB",
    boxShadow: "0 1px 2px rgba(0,0,0,0.04), 0 4px 16px rgba(0,0,0,0.03)",
    overflow: "hidden",
    marginBottom: 20,
    marginTop: 20,
  },
  tableSectionHead: {
    padding: "16px 20px 12px",
    borderBottom: "1px solid #E2E8F0",
  },
  tableSectionTitle: {
    fontSize: 14,
    fontWeight: 600,
    color: "#111827",
    letterSpacing: "-0.01em",
    fontFamily: FONT,
    margin: 0,
  },
  outerWrap: { overflow: "auto" },
  errorText: { padding: "24px", color: "#DC2626", fontSize: 13, fontFamily: FONT },
}));

export default function ForecastAnalysis({ filters = {}, onMeta }) {
  const classes = useStyles();
  const [loading, setLoading] = useState(true);
  const [error,   setError]   = useState(null);
  const [d,       setD]       = useState(null);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError(null);
    getPMRForecastAnalysisAPI(filters)
      .then((res) => {
        if (cancelled) return;
        const raw = res?.data;
        if (raw) {
          setD(mapForecastAnalysis(raw));
          onMeta?.({ actualThroughMonth: raw.actualThroughMonth, fiscalYear: raw.fiscalYear });
        } else {
          setError("Failed to load data.");
        }
      })
      .catch(() => { if (!cancelled) setError("Failed to load data."); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [JSON.stringify(filters)]); // eslint-disable-line react-hooks/exhaustive-deps

  if (loading) return <Loading />;
  if (error || !d) return <div className={classes.errorText}>{error || "No data available."}</div>;

  return (
    <div className={classes.root}>
      {d.tables.map((table) => (
        <ForecastTable key={table.title} table={table} classes={classes} />
      ))}
    </div>
  );
}
